import {
  AUTO_SWAP_LIMITS,
  ExchangeRateName,
  EXCHANGE_RATE_NAME_TO_CURRENCY,
  RegularCurrencies,
  SWAP_MIN_AMOUNTS,
  SWAP_RATE_NAME_TO_PLATFORM_FEE_PERCENT,
  SystemCurrency,
} from '@okletsplay/core';
import { ExchangeRateDto, UserBalanceDto } from 'app/interfaces/dto';
import { upperFirst } from 'lodash';
import { convertReverseCrypto } from 'shared/utils';
import { object, number, string, boolean, TestContext } from 'yup';
import { AnyObject } from 'yup/lib/types';
import { AutoSwapFormFields } from './use-auto-swap-form';

const getLimitByPair = (pair: ExchangeRateName) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return AUTO_SWAP_LIMITS[pair];
};

export const buildAutoSwapFormSchema = (
  getUserAccounts: () => (UserBalanceDto | undefined)[],
  getExchangeRates: () => (ExchangeRateDto | undefined)[]
) => {
  const getRate = (
    buyingCurrency: SystemCurrency,
    sellingCurrency: SystemCurrency,
    context: TestContext<AnyObject>
  ): number => {
    const rate = getExchangeRates().find(
      (exchange) => exchange?.from === sellingCurrency && exchange?.to === buyingCurrency
    )?.rate;

    if (!rate) {
      throw context.createError({ message: 'The value is incorrect, try again later' });
    }

    return rate;
  };

  const validateMaxAmount = (
    value: number,
    pair: ExchangeRateName,
    context: TestContext<AnyObject>
  ) => {
    const limit = getLimitByPair(pair);
    const maxAmount = limit[context.path].max;

    if (!limit || value > maxAmount) {
      throw context.createError({
        message: `${upperFirst(context.path)} amount cannot be more than ${maxAmount}`,
      });
    }
  };

  const validateMinAmount = (
    value: number,
    rate: number,
    buyingCurrency: SystemCurrency,
    sellingCurrency: SystemCurrency,
    context: TestContext<AnyObject>
  ) => {
    const minBuyingAmount = SWAP_MIN_AMOUNTS[buyingCurrency as RegularCurrencies];

    if (value < minBuyingAmount) {
      throw context.createError({
        message: `${upperFirst(
          context.path
        )} cannot be less than ${minBuyingAmount} ${buyingCurrency}`,
      });
    }

    const minSellingLimit = SWAP_MIN_AMOUNTS[sellingCurrency as RegularCurrencies];
    const sellingAmount = convertReverseCrypto(value, rate);

    if (sellingAmount < minSellingLimit) {
      throw context.createError({
        message: `${upperFirst(
          context.path
        )} cannot be less than ${minSellingLimit} ${sellingCurrency}`,
      });
    }
  };

  const validateBalance = (
    value: number,
    rate: number,
    feePercent: number,
    buyingCurrency: SystemCurrency,
    sellingCurrency: SystemCurrency,
    context: TestContext<AnyObject>
  ) => {
    const userBalance =
      getUserAccounts().find((account) => account?.currency === sellingCurrency)?.balance ?? 0;

    const sellingAmount = convertReverseCrypto(value, rate);
    const amountWithFee = sellingAmount + (sellingAmount * feePercent) / 100;

    if (userBalance < amountWithFee) {
      throw context.createError({
        message: `${buyingCurrency} amount cannot be more than equivalent of ${sellingCurrency} on balance. (+${feePercent}% platform fee)`,
      });
    }
  };

  return object().shape({
    threshold: number()
      .label('Threshold')
      .min(0)
      .test((value, context) => {
        if (!value) return false;

        const { pair } = context.parent as AutoSwapFormFields;
        const { sell: sellingCurrency, buy: buyingCurrency } = EXCHANGE_RATE_NAME_TO_CURRENCY[pair];

        const rate = getRate(buyingCurrency, sellingCurrency, context);
        const feePercent = SWAP_RATE_NAME_TO_PLATFORM_FEE_PERCENT[pair];

        validateMaxAmount(value, pair, context);
        validateMinAmount(value, rate, buyingCurrency, sellingCurrency, context);
        validateBalance(value, rate, feePercent, buyingCurrency, sellingCurrency, context);

        return true;
      }),
    purchase: number()
      .label('Purchase')
      .min(0)
      .test((value, context) => {
        if (!value) return false;

        const { pair, threshold } = context.parent as AutoSwapFormFields;

        if (!threshold || value < threshold) {
          throw context.createError({
            message: `Buying amount cannot be less than threshold amount`,
          });
        }

        const { sell: sellingCurrency, buy: buyingCurrency } = EXCHANGE_RATE_NAME_TO_CURRENCY[pair];

        const rate = getRate(buyingCurrency, sellingCurrency, context);
        const feePercent = SWAP_RATE_NAME_TO_PLATFORM_FEE_PERCENT[pair];

        validateMaxAmount(value, pair, context);
        validateMinAmount(value, rate, buyingCurrency, sellingCurrency, context);
        validateBalance(value, rate, feePercent, buyingCurrency, sellingCurrency, context);

        return true;
      }),
    pair: string().required(),
    enabled: boolean().required(),
  });
};
